import { useState, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { profileService } from '../services/profileService';
import { TUser } from '../context/UsersProvider';
import ISessionData from '../types/ISessionData';
import { handleApiError } from '../lib/errorHandler';

interface UseProfileProps {
    setProfile?: Dispatch<SetStateAction<TUser>>;
    setLoading?: Dispatch<SetStateAction<boolean>>;
    setCloseUpdateModal?: Dispatch<SetStateAction<boolean>>;
    sessionStart?: (sessionData: ISessionData) => void;
}

export function useProfileOperations(props: UseProfileProps = {}) {
    const { setProfile: externalSetProfile, setLoading: externalSetLoading, setCloseUpdateModal, sessionStart } = props;
    const [profile, setInternalProfile] = useState({} as TUser);
    const [loading, setInternalLoading] = useState(true);
    const [qrCode, setQrCode] = useState('');
    const { t } = useTranslation();

    const setProfile = externalSetProfile || setInternalProfile;
    const setLoading = externalSetLoading || setInternalLoading;

    const getProfile = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await profileService.getProfile();
            if (response.data) {
                setProfile(response.data);
                setLoading(false);
                return response.data;
            }
        } catch (error: any) {
            setLoading(false);
            const serverError = handleApiError(error, t('errors.profile.loading'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updateFullName = async (
        firstName: string,
        lastName: string,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        if (!setCloseUpdateModal) return;

        try {
            const response = await profileService.updateName(firstName, lastName);
            if (response.data) {
                setProfile(response.data);
                setCloseUpdateModal(true);
                setLoading(false);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.profile.updatingName'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updatePhoneNumber = async (
        phone: string,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        if (!setCloseUpdateModal) return;

        try {
            const response = await profileService.updatePhone(phone);
            setProfile(response.data);
            setCloseUpdateModal(true);
            setLoading(false);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.profile.updatingPhone'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const updatePassword = async (
        password: string,
        confirmPassword: string,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        if (!sessionStart || !setCloseUpdateModal) return;

        try {
            const response = await profileService.updatePassword(password, confirmPassword);
            sessionStart(response.data);
            setCloseUpdateModal(true);
            setLoading(false);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.profile.updatingPassword'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const getQR = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await profileService.getQrCode();
            const base64 = btoa(unescape(encodeURIComponent(response.data)));
            const dataUrl = `data:image/svg+xml;base64,${base64}`;
            if (response.data) {
                setQrCode(dataUrl);
                setLoading(false);
            }
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.profile.loadingQR'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const add2FaSecure = async (
        totp_token: string,
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        if (!setCloseUpdateModal) return;

        try {
            const response = await profileService.addTwoFactor(totp_token);
            setCloseUpdateModal(true);
            setProfile(response.data);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.profile.adding2FA'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    const removeQr = async (
        setAlertMessages: (msgs: Partial<Record<string, string[]>>) => void
    ) => {
        try {
            const response = await profileService.deleteQrCode();
            setProfile(response.data);
        } catch (error: any) {
            const serverError = handleApiError(error, t('errors.profile.removing2FA'), 'profile');
             setAlertMessages(serverError as Partial<Record<string, string[]>>);
        }
    };

    return {
        profile: externalSetProfile ? undefined : profile,
        loading: externalSetLoading ? undefined : loading,
        qrCode,
        setProfile,
        setLoading,
        getProfile,
        updateFullName,
        updatePhoneNumber,
        updatePassword,
        getQR,
        add2FaSecure,
        removeQr,
    };
}
